"use client";

import { PageHeader } from "@/components/ui/design-system/page-header";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";

/**
 * The feed before the requests arrive: the real grid, nine cells, and in
 * each a bone where each part of `RequestCard` goes (the frame, the brand
 * line, the title, two lines of brief, the figure and its condition, the
 * clock and the count, the button), on the tile's centre line,
 * so the page does not jump when the data lands and the shape says what is
 * coming. The header above is the real one, since its words are static;
 * the program filter is left out, because a row of segments that then
 * vanishes for a one-program creator is the jump this exists to avoid.
 *
 * MIRRORS `RequestsFeed` AND `RequestCard`. Change the grid or the card
 * there and this is the file that has to move with it.
 */
export function RequestsFeedSkeleton() {
  const t = useCreatorTranslations("requests.feed");
  return (
    <div aria-busy="true">
      {/* A plain hidden line rather than role="status": see HomeSkeleton on
          why the skeleton must not answer the auth gate test's selector. */}
      <span className="sr-only">{t("loading")}</span>
      <ul
        aria-hidden
        className="m-0 grid list-none gap-x-nf-ds-6 gap-y-nf-ds-10 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-nf-ds-10"
      >
        {/* Nine: three full rows at a desk, enough to fill a tall window so
            the data landing fills in rather than fills out. A feed with
            fewer requests shrinks below the fold, which is the cheaper
            direction; one that grows after the skeleton is the jump. */}
        {Array.from({ length: 9 }, (_, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
            key={index}
            className="flex h-full flex-col items-center"
          >
            <Skeleton className="aspect-square w-3/4 rounded-nf-ds-card sm:w-full sm:aspect-[5/6]" />
            <div className="mt-nf-ds-6 flex w-full min-w-0 flex-1 flex-col items-center gap-nf-ds-2 px-nf-ds-2">
              <div className="flex items-center gap-nf-ds-2">
                <Skeleton className="size-5 rounded-full" />
                <Skeleton className="h-[12px] w-[64px]" />
              </div>
              {/* Alternate one- and two-line titles, as a real feed does. */}
              <Skeleton className="h-[20px] w-[70%]" />
              {index % 2 === 1 ? (
                <Skeleton className="h-[20px] w-[40%]" />
              ) : null}
              <Skeleton className="h-[13px] w-[85%]" />
              <Skeleton className="h-[13px] w-[60%]" />
              <div className="mt-nf-ds-2 flex items-baseline gap-nf-ds-2">
                <Skeleton className="h-[26px] w-[84px]" />
                <Skeleton className="h-[13px] w-[132px]" />
              </div>
              <Skeleton className="h-[12px] w-[160px]" />
              <Skeleton className="mt-auto h-nf-ds-control w-[132px] rounded-nf-ds-pill" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The feed page's column, shared with `RequestsFeed`. */
export const REQUESTS_FEED_COLUMN =
  "mx-auto flex w-full max-w-[1120px] flex-1 flex-col gap-nf-ds-6 pt-nf-ds-4 lg:py-nf-ds-6";

/**
 * The whole feed page while the portal itself boots, for the shell
 * skeleton: the real header, since its words are static, over the grid.
 */
export function RequestsFeedPageSkeleton() {
  const t = useCreatorTranslations("requests.feed");
  return (
    <div className={REQUESTS_FEED_COLUMN}>
      <PageHeader title={t("title")} subtitle={t("subtitle")} />
      <RequestsFeedSkeleton />
    </div>
  );
}
